import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { RevealMask } from "@/components/motion/Reveal";
import ScrambleText from "@/components/motion/ScrambleText";
import { clientLogos, clientCategories, clientsBySector } from "@/data/content";

function chunk(arr, parts) {
  const size = Math.ceil(arr.length / parts);
  return Array.from({ length: parts }, (_, i) => arr.slice(i * size, (i + 1) * size)).filter((r) => r.length);
}

function LogoTile({ id }) {
  return (
    <div className="mx-4 flex h-24 w-40 items-center justify-center rounded-sm border border-line/70 bg-surface p-4 transition-colors duration-500 hover:border-accent md:mx-6 md:h-28 md:w-52">
      <img
        src={`/images/clients/${id}.webp`}
        alt={`Client ${id}`}
        loading="lazy"
        decoding="async"
        className="max-h-full max-w-full object-contain opacity-70 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0"
      />
    </div>
  );
}

export default function ClientsWall() {
  const [active, setActive] = useState("all");
  const ids = active === "all" ? clientLogos : clientsBySector[active] || [];
  const rows = chunk(ids, Math.min(3, Math.max(1, Math.ceil(ids.length / 12))));

  return (
    <section data-testid="clients-wall" className="border-y border-line bg-bone py-16 sm:py-20 md:py-24 lg:py-28">
      <div className="mx-auto mb-10 max-w-[1600px] px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <ScrambleText
          as="p"
          text="Selected Clients"
          className="mb-4 block text-xs font-bold uppercase tracking-[0.22em] text-accent"
        />
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <RevealMask
            as="h2"
            className="max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-ink md:text-6xl"
          >
            From luxury fashion and fine jewellery to F&amp;B, real estate, power and public policy.
          </RevealMask>
        </div>

        <div
          data-testid="clients-cat-tabs"
          className="mt-8 flex flex-wrap gap-2 md:gap-3"
        >
          {clientCategories.map((c) => {
            const isActive = active === c.slug;
            return (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActive(c.slug)}
                data-testid={`clients-cat-tab-${c.slug}`}
                aria-pressed={isActive}
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors duration-300 md:text-sm ${
                  isActive
                    ? "border-ink bg-ink text-bone"
                    : "border-line text-muted hover:border-ink hover:text-ink"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          data-testid={`clients-wall-rows-${active}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="space-y-6"
        >
          {rows.map((row, i) => (
            <Marquee
              key={i}
              speed={22 + i * 6}
              direction={i % 2 === 0 ? "left" : "right"}
              gradient={false}
              pauseOnHover
              className="marquee-track"
            >
              {row.map((id) => (
                <LogoTile key={id} id={id} />
              ))}
            </Marquee>
          ))}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
